import { ReactElement } from "react";
import { AddonParams, BaseAddon } from "../base/BaseAddon";
import { StepChangeEvent, StepType } from "../../types/types";
import { UploadStep } from "../../components/UploadStep/UploadStep";
import { WorkBook } from "xlsx-ugnis";
import { TOASTER_TOAST_TIME } from "../../../../../core/toaster/Toaster_model";
import toastService from "../../../../../core/toaster/ToasterService";
import { exceedsMaxRecords } from "../../utils/exceedsMaxRecords";
import I18n from "../../../../../core/localization/I18n";
import { mapWorkbook } from "../../utils/mapWorkbook";

export class UploadAddon<T extends string = string> extends BaseAddon<T> {
  constructor(onNext: (event: StepChangeEvent) => void, params: AddonParams<T>) {
    super("UploadAddon", [StepType.upload], onNext, params);
  }

  protected getJSXElement(step: StepType, data: any): ReactElement {
    return (
      <UploadStep
        gridType={this.gridType}
        fields={this.fields}
        onContinue={async (workbook, file) => {
          this.setUploadedFile(file);
          await this.afterUpload(workbook, file);
        }}
      />
    );
  }

  async afterUpload(workbook: WorkBook, file: File): Promise<void> {
    if (this.maxFileSize && file.size > this.maxFileSize) {
      toastService.showErrorToast(TOASTER_TOAST_TIME.NORMAL, "right").setContent(
        <span>
          {I18n.t("phrases.uploadStepMaxFileSizeExceeded", {
            size: (this.maxFileSize / (1024 * 1024)).toFixed(2)
          })}
        </span>
      );
      return;
    }

    const isSingleSheet = workbook.SheetNames.length === 1;
    if (isSingleSheet) {
      if (this.maxRecords && exceedsMaxRecords(workbook.Sheets[workbook.SheetNames[0]], this.maxRecords)) {
        toastService
          .showErrorToast(TOASTER_TOAST_TIME.NORMAL, "right")
          .setContent(
            <span>{I18n.t("phrases.uploadStepMaxRecordsExceeded", { name: this.maxRecords?.toString() ?? "" })}</span>
          );
        return;
      }

      try {
        const rawData = mapWorkbook(workbook);

        let processedData = rawData;
        if (this.uploadStepHook) {
          processedData = await this.uploadStepHook(rawData);
        }

        this.onNext({
          type: StepType.selectHeader,
          data: processedData,
          file,
          workbook
        });
      } catch (e) {
        console.error("Error processing workbook:", e);
        toastService.showErrorToast(TOASTER_TOAST_TIME.NORMAL, "right").setContent(<span>{e.message}</span>);
      }
    } else {
      this.onNext({
        type: StepType.selectSheet,
        workbook,
        file
      });
    }
  }
}
